import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../pages/dashboard/DashboardUI.module.css';
import settingsStyles from '../../pages/dashboard/Settings.module.css';
import {
  getProjectStatuses,
  createProjectStatus,
  updateProjectStatus,
  deleteProjectStatus,
  reorderProjectStatuses,
} from '../../services/projectService';
import ConfirmDialog from '../ConfirmDialog';
import ReorderListModal from '../ReorderListModal';

const COLOR_PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#84cc16',
  '#6366f1', '#14b8a6', '#ef4444', '#6b7280'
];

const COLOR_NAMES = [
  'Blue', 'Emerald', 'Amber', 'Violet',
  'Pink', 'Cyan', 'Orange', 'Lime',
  'Indigo', 'Teal', 'Red', 'Gray'
];

function getTextColor(bgColor) {
  if (!bgColor) return '#ffffff';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}

export default function ProjectStatusManager({ onMessage }) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    color: '#3b82f6',
    description: ''
  });

  const loadStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProjectStatuses();
      setStatuses(data.statuses || []);
    } catch (error) {
      onMessage({ message: error.message || 'Failed to load project statuses', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const openCreateModal = () => {
    setEditingStatus(null);
    setFormData({ name: '', color: '#3b82f6', description: '' });
    setShowModal(true);
  };

  const openEditModal = (status) => {
    setEditingStatus(status);
    setFormData({
      name: status.name,
      color: status.color || '#6b7280',
      description: status.description || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStatus(null);
    setFormData({ name: '', color: '#3b82f6', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onMessage({ message: 'Status name is required', type: 'error' });
      return;
    }

    try {
      if (editingStatus) {
        await updateProjectStatus(editingStatus.status_id, {
          name: formData.name.trim(),
          color: formData.color,
          description: formData.description.trim()
        });
        onMessage({ message: 'Status updated successfully', type: 'success' });
      } else {
        await createProjectStatus({
          name: formData.name.trim(),
          color: formData.color,
          description: formData.description.trim()
        });
        onMessage({ message: 'Status created successfully', type: 'success' });
      }
      closeModal();
      await loadStatuses();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to save status';
      onMessage({ message: msg, type: 'error' });
    }
  };

  const handleDelete = async (status) => {
    if (deleting === status.status_id) return;
    setDeleting(status.status_id);
    try {
      await deleteProjectStatus(status.status_id);
      onMessage({ message: `Status "${status.name}" deleted`, type: 'success' });
      await loadStatuses();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to delete status';
      onMessage({ message: msg, type: 'error' });
    } finally {
      setDeleting(null);
      setConfirmDelete(null);
    }
  };

  const handleSaveReorder = async (orderedStatusIds) => {
    try {
      setSavingOrder(true);
      const response = await reorderProjectStatuses(orderedStatusIds);
      setStatuses(response.statuses || []);
      setShowReorderModal(false);
      onMessage({ message: 'Project status order updated', type: 'success' });
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to reorder statuses';
      onMessage({ message: msg, type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  if (loading) {
    return <div className={settingsStyles.loading}>Loading project statuses...</div>;
  }

  return (
    <div>
      <div className={settingsStyles.sectionHeader}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            Project Statuses
          </h3>
          <p className={settingsStyles.cardDesc} style={{ margin: '4px 0 0 0' }}>
            Manage project statuses used for grouping, filtering, and tracking progress.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {statuses.length > 1 && (
            <button className={styles.buttonSecondary} onClick={() => setShowReorderModal(true)}>
              Reorder
            </button>
          )}
          <button className={styles.buttonPrimary} onClick={openCreateModal}>
            + New Status
          </button>
        </div>
      </div>

      {statuses.length === 0 ? (
        <div className={settingsStyles.emptyState}>
          <p>No project statuses found.</p>
        </div>
      ) : (
        <div className={settingsStyles.labelsList}>
          {statuses.map((status) => (
            <div key={status.status_id} className={settingsStyles.labelCard}>
              <div className={settingsStyles.labelInfo}>
                <span
                  className={settingsStyles.labelBadge}
                  style={{
                    backgroundColor: status.color || '#6b7280',
                    color: getTextColor(status.color)
                  }}
                >
                  {status.name}
                </span>
                {status.is_default && (
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
                    Default
                  </span>
                )}
                {status.description && (
                  <span className={settingsStyles.labelDescription}>
                    {status.description}
                  </span>
                )}
              </div>
              <div className={settingsStyles.roleActions}>
                <button
                  className={styles.buttonSecondary}
                  onClick={() => openEditModal(status)}
                >
                  Edit
                </button>
                {!status.is_default && (
                  <button
                    className={styles.buttonDanger}
                    onClick={() => setConfirmDelete(status)}
                    disabled={deleting === status.status_id}
                  >
                    {deleting === status.status_id ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className={settingsStyles.modalOverlay} onClick={closeModal}>
          <div className={settingsStyles.modal} onClick={(e) => e.stopPropagation()} style={{ minHeight: 'auto' }}>
            <div className={settingsStyles.modalHeader}>
              <h3>{editingStatus ? 'Edit Status' : 'Create Status'}</h3>
              <button className={settingsStyles.closeButton} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className={settingsStyles.modalContent}>
              <form onSubmit={handleSubmit}>
                <div className={settingsStyles.formGroup}>
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. On Hold, In Review, QA"
                    autoFocus
                    disabled={editingStatus?.is_default}
                  />
                  {editingStatus?.is_default && (
                    <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: '4px 0 0' }}>
                      Default status names cannot be changed.
                    </p>
                  )}
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Color</label>
                  <div className={settingsStyles.colorPicker}>
                    {COLOR_PALETTE.map((color, index) => (
                      <button
                        key={color}
                        type="button"
                        className={`${settingsStyles.colorSwatch} ${formData.color === color ? settingsStyles.colorSwatchActive : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({ ...formData, color })}
                        title={COLOR_NAMES[index]}
                        aria-label={COLOR_NAMES[index]}
                      />
                    ))}
                  </div>
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Preview</label>
                  <div>
                    <span
                      className={settingsStyles.labelBadge}
                      style={{
                        backgroundColor: formData.color,
                        color: getTextColor(formData.color)
                      }}
                    >
                      {formData.name || 'Status Preview'}
                    </span>
                  </div>
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What does this status indicate?"
                    rows={2}
                  />
                </div>

                <div className={settingsStyles.modalActions}>
                  <button className={styles.buttonSecondary} onClick={closeModal} type="button">
                    Cancel
                  </button>
                  <button className={styles.buttonPrimary} type="submit">
                    {editingStatus ? 'Save Changes' : 'Create Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          message={`Are you sure you want to delete the "${confirmDelete.name}" status? Projects using this status will be moved to the first available status.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      <ReorderListModal
        isOpen={showReorderModal}
        title="Reorder Project Statuses"
        description="Drag statuses into the sequence you want. This order is used in project status lists and filters."
        items={statuses}
        getId={(item) => item.status_id}
        getLabel={(item) => item.name}
        getColor={(item) => item.color}
        onClose={() => setShowReorderModal(false)}
        onSave={handleSaveReorder}
        saving={savingOrder}
      />
    </div>
  );
}
